/**
 * /build — a facilitator's own scenarios; /build/:id — the editor.
 *
 * The editor keeps the working copy in memory and saves it 800 ms after the last edit (and on
 * Ctrl/Cmd+S, and before publishing or translating), with the `updatedAt` it last saw so a save
 * from another tab is caught as a conflict rather than overwritten. The same pure checks the API
 * runs (`validateScenario`, `checkConventions`, the Zod schema) run here on every change, so the
 * checks panel is live and a schema error never leaves the page. Unsaved edits are mirrored to
 * sessionStorage so a reload offers them back.
 */
import { storageKey } from "@shared/brand";
import { formatDateTime, LOCALES } from "@shared/i18n";
import {
  checkConventions,
  flattenTranslatable,
  playthroughSummary,
  type Scenario,
  type ScenarioMeta,
  Scenario as ScenarioSchema,
  type ValidationIssue,
  type Visibility,
  validateScenario,
} from "@shared/scenario";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Page } from "../components/Header";
import { Icon } from "../components/Icon";
import { Button, Card, Dialog, Pill, Spinner, toast } from "../components/ui";
import { useI18n } from "../i18n";
import { type ApiError, api, del, get, patch, post } from "../lib/api";
import { type CatalogueEntry, forkScenario, listScenarios } from "../lib/scenario";
import { Link, navigate } from "../router";
import { type Checks, ChecksPanel, type TranslationStatus } from "./build/ChecksPanel";
import { FieldErrors } from "./build/fields";
import {
  freshId,
  issuesFor,
  NODE_TYPES,
  newNode,
  outlineOrder,
  SECTIONS,
  type SectionId,
  type Selection,
  sameSelection,
  selectionForPath,
} from "./build/model";
import { type Edit, NodeForm } from "./build/NodeForm";
import { SectionForm } from "./build/SectionForms";
import { type FacilitatorInfo, HostNav } from "./HostDashboard";

const LOCALE_COUNT = LOCALES.length;

/* ------------------------------------------------ list ------------------------------------------------ */

function statusLabel(
  t: (k: string, p?: Record<string, string | number>) => string,
  status: string | undefined,
  publishedVersion: string | null | undefined,
  unpublished: boolean | undefined,
): string {
  if (status !== "published" || !publishedVersion) return t("build.status.draft");
  return unpublished
    ? t("build.status.publishedChanges")
    : t("build.status.publishedVersion", { version: publishedVersion });
}

function visibilityLabel(
  t: (k: string, p?: Record<string, string | number>) => string,
  visibility: Visibility | undefined,
  approved: boolean,
): string {
  if (visibility === "listed") return approved ? t("build.visibility.listed") : t("build.visibility.pending");
  return t(`build.visibility.${visibility ?? "private"}`);
}

export function BuildList({
  facilitator,
  onSignOut,
}: {
  facilitator: FacilitatorInfo;
  onSignOut: () => void;
}) {
  const { t, locale } = useI18n();
  const [all, setAll] = useState<CatalogueEntry[] | null>(null);
  const [picker, setPicker] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [importError, setImportError] = useState<{ message: string; issues: ValidationIssue[] } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    listScenarios(locale)
      .then((r) => setAll(r.scenarios))
      .catch(() => setAll([]));
  }, [locale]);

  const mine = (all ?? [])
    .filter((s) => s.origin === "mine")
    .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));

  const copy = async (id: string) => {
    setBusy(id);
    try {
      const res = await forkScenario(id);
      navigate(`/build/${res.scenario.id}`);
    } catch (e) {
      toast((e as ApiError).message, "error");
      setBusy(null);
    }
  };

  const importFile = async (file: File) => {
    setImportError(null);
    setBusy("import");
    try {
      let doc: unknown;
      try {
        doc = JSON.parse(await file.text());
      } catch {
        setImportError({ message: t("build.import.notJson"), issues: [] });
        setBusy(null);
        return;
      }
      const res = await post<{ scenario: CatalogueEntry }>("/api/scenarios/import", doc);
      navigate(`/build/${res.scenario.id}`);
    } catch (e) {
      const err = e as ApiError;
      const body = err.body as { issues?: ValidationIssue[] } | undefined;
      setImportError({
        message:
          err.code === "invalidDocument"
            ? t("build.import.invalidDocument")
            : err.code === "invalidScenario"
              ? t("build.import.invalidScenario")
              : err.message,
        issues: body?.issues ?? [],
      });
      setBusy(null);
    }
  };

  return (
    <Page>
      <HostNav facilitator={facilitator} onSignOut={onSignOut} section="build" />
      <main id="main" class="stack" style={{ paddingBlock: "var(--s-4) var(--s-12)" }}>
        <div class="row between">
          <h1 style={{ margin: 0 }}>{t("build.list.title")}</h1>
          <div class="row">
            <Button
              variant="secondary"
              icon="file-text"
              disabled={busy !== null}
              onClick={() => fileRef.current?.click()}
            >
              {busy === "import" ? t("build.import.busy") : t("build.import")}
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              class="visually-hidden"
              tabIndex={-1}
              aria-hidden="true"
              onChange={(e) => {
                const input = e.currentTarget as HTMLInputElement;
                const file = input.files?.[0];
                input.value = "";
                if (file) importFile(file);
              }}
            />
            <Button icon="plus" disabled={busy !== null} onClick={() => setPicker(true)}>
              {t("build.new")}
            </Button>
          </div>
        </div>
        <p class="muted" style={{ margin: 0 }}>
          {t("build.list.lead")}
        </p>
        {importError ? (
          <div class="card signal tight stack-sm" role="alert">
            <strong>{importError.message}</strong>
            {importError.issues.length ? (
              <ul class="small build-import-issues">
                {importError.issues.slice(0, 8).map((i, n) => (
                  <li key={n}>
                    <code class="build-wrap">{i.path || "—"}</code> {i.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        {!all ? (
          <Spinner />
        ) : mine.length === 0 ? (
          <div class="empty stack-sm">
            <p>{t("build.list.empty")}</p>
            <div class="row" style={{ justifyContent: "center" }}>
              <Button icon="plus" onClick={() => setPicker(true)}>
                {t("build.new")}
              </Button>
            </div>
          </div>
        ) : (
          <div class="grid grid-2">
            {mine.map((s) => {
              const done = LOCALES.filter((l) => (s.translations[l.tag] ?? 0) >= 0.999).length;
              return (
                <Card key={s.id} class="tight stack-sm build-list-card">
                  <div class="row">
                    <Pill tone={s.status === "published" ? "accent" : undefined}>
                      {statusLabel(t, s.status, s.publishedVersion, s.unpublishedChanges)}
                    </Pill>
                    <Pill tone={s.visibility === "private" ? undefined : "signal"}>
                      {visibilityLabel(t, s.visibility, Boolean(s.approved))}
                    </Pill>
                  </div>
                  <h3 style={{ margin: 0 }} dir="auto">
                    {s.title}
                  </h3>
                  <p class="fg-2 small" style={{ margin: 0 }}>
                    {s.tagline}
                  </p>
                  <p class="muted small" style={{ margin: 0 }}>
                    {s.sourceTitle ? <>{t("build.basedOn", { title: s.sourceTitle })} · </> : null}
                    {done >= LOCALE_COUNT
                      ? t("build.list.languagesAll", { count: LOCALE_COUNT })
                      : t("build.list.languages", { done, total: LOCALE_COUNT })}
                    {s.updatedAt ? (
                      <> · {t("build.list.updated", { when: formatDateTime(locale, s.updatedAt) })}</>
                    ) : null}
                  </p>
                  <div class="row">
                    <Link href={`/build/${s.id}`} class="btn sm">
                      <Icon name="settings" /> {t("build.edit")}
                    </Link>
                    <Link
                      href={`/host/new?scenario=${encodeURIComponent(s.id)}&draft=1`}
                      class="btn secondary sm"
                    >
                      <Icon name="play" /> {t("build.runTest")}
                    </Link>
                    <a
                      href={`/api/scenarios/${encodeURIComponent(s.id)}/export`}
                      download
                      class="btn ghost sm"
                    >
                      <Icon name="file-text" /> {t("build.export")}
                    </a>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
      {picker ? (
        <Dialog title={t("build.new")} onClose={() => setPicker(false)}>
          <div class="stack">
            <p class="muted small">{t("build.new.lead")}</p>
            {(["workplace", "community", "youth"] as const).map((aud) => {
              const items = (all ?? []).filter(
                (s) => s.origin === "catalogue" && (s.audience ?? "workplace") === aud,
              );
              if (!items.length) return null;
              return (
                <div key={aud} class="choice-group">
                  <div class="choice-group-head">
                    <span class="eyebrow">{t(`audience.${aud}`)}</span>
                  </div>
                  <ul class="build-pick">
                    {items.map((s) => (
                      <li key={s.id} class="build-pick-row">
                        <div class="build-pick-text">
                          <strong>{s.title}</strong>
                          <span class="muted small">{s.tagline}</span>
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          icon="copy"
                          disabled={busy !== null}
                          onClick={() => copy(s.id)}
                        >
                          {busy === s.id ? t("build.copying") : t("build.copy")}
                          <span class="visually-hidden"> {s.title}</span>
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </Dialog>
      ) : null}
    </Page>
  );
}

/* ----------------------------------------------- editor ----------------------------------------------- */

interface DraftResponse {
  meta: ScenarioMeta;
  scenario: Scenario;
  issues: { errors: ValidationIssue[]; warnings: ValidationIssue[] };
  translations: Record<string, TranslationStatus>;
  translationModel: string | null;
}

interface SaveResponse {
  meta: ScenarioMeta;
  issues: { errors: ValidationIssue[]; warnings: ValidationIssue[] };
  translations: Record<string, TranslationStatus>;
}

type SaveState = "saved" | "dirty" | "saving" | "invalid" | "error" | "conflict";

interface Backup {
  scenario: Scenario;
  at: number;
}

const backupKey = (id: string) => storageKey(`build.${id}`);

function readBackup(id: string): Backup | null {
  try {
    const raw = sessionStorage.getItem(backupKey(id));
    return raw ? (JSON.parse(raw) as Backup) : null;
  } catch {
    return null;
  }
}
function writeBackup(id: string, scenario: Scenario): void {
  try {
    sessionStorage.setItem(backupKey(id), JSON.stringify({ scenario, at: Date.now() }));
  } catch {
    /* storage unavailable: the autosave still runs */
  }
}
function clearBackup(id: string): void {
  try {
    sessionStorage.removeItem(backupKey(id));
  } catch {
    /* storage unavailable */
  }
}

function zodIssues(scenario: Scenario, id: string): ValidationIssue[] {
  const parsed = ScenarioSchema.safeParse({ ...scenario, id });
  if (parsed.success) return [];
  return parsed.error.issues.map((i) => ({
    level: "error" as const,
    path: i.path.map(String).join("."),
    message: i.message,
  }));
}

/** The same checks the API runs, split the way the panel shows them. */
function runChecks(scenario: Scenario, id: string): Checks & { schema: ValidationIssue[] } {
  const schema = zodIssues(scenario, id);
  const parsed = ScenarioSchema.safeParse({ ...scenario, id });
  const body = parsed.success ? parsed.data : scenario;
  let graph: ValidationIssue[] = [];
  let conventions: ValidationIssue[] = [];
  let play: Checks["play"] = null;
  try {
    graph = validateScenario(body);
  } catch {
    graph = [];
  }
  try {
    conventions = checkConventions(body);
    play = playthroughSummary(body);
  } catch {
    conventions = [];
  }
  return {
    schema,
    errors: [...schema, ...graph.filter((i) => i.level === "error")],
    warnings: graph.filter((i) => i.level === "warning"),
    conventions,
    play,
  };
}

function timeOf(locale: string, ts: number): string {
  try {
    return new Intl.DateTimeFormat(locale, { timeStyle: "short" }).format(new Date(ts));
  } catch {
    return new Date(ts).toISOString().slice(11, 16);
  }
}

export function BuildEditor({
  id,
  facilitator,
  onSignOut,
}: {
  id: string;
  facilitator: FacilitatorInfo;
  onSignOut: () => void;
}) {
  const { t } = useI18n();
  const [loaded, setLoaded] = useState<DraftResponse | null>(null);
  const [failed, setFailed] = useState<"notFound" | "error" | null>(null);

  // Depends on the id alone: re-fetching when the locale catalogue arrives would remount the editor.
  const load = useCallback(async () => {
    try {
      setLoaded(await get<DraftResponse>(`/api/scenarios/${encodeURIComponent(id)}/draft`));
    } catch (e) {
      setFailed((e as ApiError).status === 404 ? "notFound" : "error");
    }
  }, [id]);

  useEffect(() => {
    setLoaded(null);
    setFailed(null);
    load();
  }, [load]);

  if (failed) {
    return (
      <Page>
        <HostNav facilitator={facilitator} onSignOut={onSignOut} section="build" />
        <main id="main" class="stack" style={{ paddingBlock: "var(--s-8)" }}>
          <h1 style={{ margin: 0 }}>{failed === "notFound" ? t("build.missing.title") : t("error.title")}</h1>
          <p>{failed === "notFound" ? t("build.missing.body") : t("error.generic")}</p>
          <div>
            <Link href="/build" class="btn secondary">
              <Icon name="arrow-left" /> {t("build.list.title")}
            </Link>
          </div>
        </main>
      </Page>
    );
  }
  if (!loaded) {
    return (
      <Page>
        <HostNav facilitator={facilitator} onSignOut={onSignOut} section="build" />
        <main id="main" style={{ paddingBlock: "var(--s-8)" }}>
          <Spinner />
        </main>
      </Page>
    );
  }
  return (
    <Editor
      key={`${loaded.meta.id}-${loaded.meta.updatedAt}`}
      initial={loaded}
      facilitator={facilitator}
      onSignOut={onSignOut}
      reload={load}
    />
  );
}

function Editor({
  initial,
  facilitator,
  onSignOut,
  reload,
}: {
  initial: DraftResponse;
  facilitator: FacilitatorInfo;
  onSignOut: () => void;
  reload: () => Promise<void>;
}) {
  const { t, locale } = useI18n();
  const id = initial.meta.id;
  const [draft, setDraft] = useState<Scenario>(initial.scenario);
  const [meta, setMeta] = useState<ScenarioMeta>(initial.meta);
  const [statuses, setStatuses] = useState<Record<string, TranslationStatus>>(initial.translations);
  const [saveState, setSaveState] = useState<SaveState>("saved");
  const [savedAt, setSavedAt] = useState<number | null>(initial.meta.updatedAt);
  const [rejected, setRejected] = useState<ValidationIssue[]>([]);
  const [conflict, setConflict] = useState<{ meta: ScenarioMeta | null } | null>(null);
  const [backup, setBackup] = useState<Backup | null>(() => {
    const b = readBackup(id);
    if (!b) return null;
    if (JSON.stringify(b.scenario) === JSON.stringify(initial.scenario)) {
      clearBackup(id);
      return null;
    }
    return b;
  });
  const [selection, setSelection] = useState<Selection>({ kind: "section", id: "overview" });
  // Under 1100 px the regions are tabs; a phone starts on the outline, the way a list precedes its
  // detail, and choosing a row opens the form.
  const [tab, setTab] = useState<"outline" | "edit" | "checks">("outline");
  const [focusPath, setFocusPath] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const draftRef = useRef(draft);
  const editSeq = useRef(0);
  const savedSeq = useRef(0);
  const expected = useRef<number>(initial.meta.updatedAt);
  const inflight = useRef<Promise<boolean> | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  const checks = useMemo(() => runChecks(draft, id), [draft, id]);
  const fieldErrors = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of checks.schema.length ? checks.schema : rejected)
      if (!m.has(i.path)) m.set(i.path, i.message);
    return m;
  }, [checks.schema, rejected]);
  const shownChecks: Checks = useMemo(
    () =>
      checks.schema.length || !rejected.length
        ? checks
        : { ...checks, errors: [...rejected, ...checks.errors] },
    [checks, rejected],
  );
  const totalStrings = useMemo(() => Object.keys(flattenTranslatable(draft)).length, [draft]);

  /** Save now if anything changed since the last save; resolves true when the server has the draft. */
  const save = useCallback(async (): Promise<boolean> => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (inflight.current) await inflight.current;
    if (editSeq.current === savedSeq.current) return true;
    const seq = editSeq.current;
    const body = draftRef.current;
    if (zodIssues(body, id).length) {
      setSaveState("invalid");
      return false;
    }
    setSaveState("saving");
    const run = (async () => {
      try {
        const res = await api<SaveResponse>(`/api/scenarios/${encodeURIComponent(id)}/draft`, {
          method: "PUT",
          json: { scenario: body, expectedUpdatedAt: expected.current },
        });
        expected.current = res.meta.updatedAt;
        savedSeq.current = seq;
        setMeta(res.meta);
        setStatuses(res.translations);
        setRejected([]);
        setSavedAt(Date.now());
        if (editSeq.current === seq) {
          setSaveState("saved");
          clearBackup(id);
        } else {
          setSaveState("dirty");
        }
        return true;
      } catch (e) {
        const err = e as ApiError;
        if (err.status === 409) {
          setConflict({ meta: (err.body as { meta: ScenarioMeta | null }).meta ?? null });
          setSaveState("conflict");
        } else if (err.status === 400) {
          setRejected((err.body as { issues?: ValidationIssue[] }).issues ?? []);
          setSaveState("invalid");
        } else {
          setSaveState("error");
        }
        return false;
      }
    })();
    inflight.current = run;
    const ok = await run;
    inflight.current = null;
    // Edits made while this save was in flight get their own save.
    if (ok && editSeq.current !== savedSeq.current && !timer.current) {
      timer.current = setTimeout(() => {
        save();
      }, 800);
    }
    return ok;
  }, [id]);

  const edit: Edit = useCallback(
    (fn) => {
      const next = structuredClone(draftRef.current);
      const out = fn(next) as Scenario | undefined;
      const value = out ?? next;
      draftRef.current = value;
      setDraft(value);
      editSeq.current++;
      setRejected([]);
      setSaveState((s) => (s === "conflict" ? s : "dirty"));
      writeBackup(id, value);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        save();
      }, 800);
    },
    [id, save],
  );

  // Ctrl/Cmd+S saves now; leaving with unsaved edits asks first (the session backup is the net).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        save();
      }
    };
    const onUnload = (e: BeforeUnloadEvent) => {
      if (editSeq.current !== savedSeq.current) e.preventDefault();
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("beforeunload", onUnload);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [save]);

  // The side columns stick under the header, whose height changes as its pills and buttons wrap.
  useEffect(() => {
    const el = headRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      document.documentElement.style.setProperty("--build-head", `${el.offsetHeight}px`);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--build-head");
    };
  }, []);

  // After a jump from a finding, bring the field at fault into view and put the caret in it.
  useEffect(() => {
    if (!focusPath) return;
    const root = formRef.current;
    if (!root) return;
    const parts = focusPath.split(".");
    let target: HTMLElement | null = null;
    for (let n = parts.length; n > 0 && !target; n--) {
      const p = parts.slice(0, n).join(".");
      target = root.querySelector<HTMLElement>(`[data-path="${CSS.escape(p)}"]`);
    }
    const el = target ?? root;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const field = el.matches("input, textarea, select")
      ? el
      : el.querySelector<HTMLElement>("input:not([type=hidden]), textarea, select, button");
    field?.focus({ preventScroll: true });
    setFocusPath(null);
  }, [focusPath]);

  const select = (sel: Selection) => {
    setSelection(sel);
    setTab("edit");
    // Only scroll when the top of the form (or, on a phone, the tabs) has gone above the viewport.
    const wide = window.matchMedia("(min-width: 1100px)").matches;
    const target = wide ? formRef.current : tabsRef.current;
    if (target && target.getBoundingClientRect().top < 0) target.scrollIntoView({ block: "start" });
  };
  const jump = (path: string) => {
    setSelection(selectionForPath(draftRef.current, path));
    setTab("edit");
    setFocusPath(path || "title");
  };

  // A selection that no longer exists (a deleted or renamed node) falls back to the overview.
  const current: Selection =
    selection.kind === "node" && !draft.nodes[selection.id] ? { kind: "section", id: "overview" } : selection;

  const publish = async () => {
    setPublishing(true);
    try {
      if (!(await save())) {
        toast(t("build.publish.saveFirst"), "error");
        return;
      }
      const res = await post<{ meta: ScenarioMeta; version: string }>(
        `/api/scenarios/${encodeURIComponent(id)}/publish`,
      );
      expected.current = res.meta.updatedAt;
      setMeta(res.meta);
      // The server writes the published version back into the draft; mirror it without a save.
      draftRef.current = { ...draftRef.current, version: res.version };
      setDraft(draftRef.current);
      toast(t("build.publish.done", { version: res.version }), "success");
    } catch (e) {
      const err = e as ApiError;
      toast(err.status === 422 ? t("build.publish.blocked") : err.message, "error");
    } finally {
      setPublishing(false);
    }
  };

  const setVisibility = async (visibility: Visibility) => {
    try {
      const res = await patch<{ meta: ScenarioMeta }>(`/api/scenarios/${encodeURIComponent(id)}`, {
        visibility,
      });
      setMeta((m) => ({
        ...res.meta,
        updatedAt: m.updatedAt > res.meta.updatedAt ? m.updatedAt : res.meta.updatedAt,
      }));
    } catch (e) {
      toast((e as ApiError).message, "error");
    }
  };

  const remove = async () => {
    try {
      await del(`/api/scenarios/${encodeURIComponent(id)}`);
      clearBackup(id);
      editSeq.current = savedSeq.current;
      toast(t("build.deleted", { title: draft.title }), "success");
      navigate("/build");
    } catch (e) {
      toast((e as ApiError).message, "error");
    }
  };

  const addNode = (type: Scenario["nodes"][string]["type"]) => {
    const nid = freshId("node", Object.keys(draftRef.current.nodes));
    edit((s) => {
      s.nodes[nid] = newNode(type, s);
    });
    setAdding(false);
    select({ kind: "node", id: nid });
  };

  const errorCount = checks.errors.length + (checks.schema.length ? 0 : rejected.length);
  const unpublished = !meta.publishedAt || meta.updatedAt > meta.publishedAt;
  const saveText =
    saveState === "saving"
      ? t("build.save.saving")
      : saveState === "dirty"
        ? t("build.save.dirty")
        : saveState === "invalid"
          ? t("build.save.invalid")
          : saveState === "error"
            ? t("build.save.error")
            : saveState === "conflict"
              ? t("build.save.conflict")
              : savedAt
                ? t("build.save.saved", { time: timeOf(locale, savedAt) })
                : t("build.save.savedPlain");

  return (
    <Page wide>
      <HostNav facilitator={facilitator} onSignOut={onSignOut} section="build" />
      <main id="main" class="build-main">
        <div class="build-head" ref={headRef}>
          <div class="build-head-title">
            <Link href="/build" class="btn ghost sm build-back" aria-label={t("build.list.title")}>
              <Icon name="arrow-left" />
            </Link>
            <h1 dir="auto">{draft.title || id}</h1>
            <span class="build-head-pills">
              <Pill tone={meta.status === "published" ? "accent" : undefined}>
                {statusLabel(t, meta.status, meta.publishedVersion, unpublished)}
              </Pill>
              <button
                type="button"
                class={`pill build-pill-button ${meta.visibility === "private" ? "" : "signal"}`}
                onClick={() => {
                  setTab("checks");
                  requestAnimationFrame(() =>
                    document
                      .getElementById("build-sharing")
                      ?.scrollIntoView({ block: "start", behavior: "smooth" }),
                  );
                }}
              >
                {visibilityLabel(t, meta.visibility, meta.approvedAt !== null)}
              </button>
            </span>
          </div>
          <div class="build-head-actions">
            <span class={`build-save ${saveState}`} role="status" aria-live="polite">
              <span class="build-save-dot" aria-hidden="true" />
              {saveText}
              {saveState === "error" ? (
                <button type="button" class="build-linkish" onClick={() => save()}>
                  {t("action.retry")}
                </button>
              ) : null}
            </span>
            <Link href={`/host/new?scenario=${encodeURIComponent(id)}&draft=1`} class="btn secondary sm">
              <Icon name="play" /> {t("build.runTest")}
            </Link>
            <a href={`/api/scenarios/${encodeURIComponent(id)}/export`} download class="btn ghost sm">
              <Icon name="file-text" /> {t("build.export")}
            </a>
            <Button
              size="sm"
              icon="badge-check"
              disabled={publishing || errorCount > 0}
              aria-describedby="build-publish-why"
              title={errorCount ? t("build.publish.errors", { count: errorCount }) : undefined}
              onClick={publish}
            >
              {publishing ? t("build.publish.busy") : t("build.publish")}
            </Button>
            <span id="build-publish-why" class="visually-hidden">
              {errorCount ? t("build.publish.errors", { count: errorCount }) : t("build.publish.ready")}
            </span>
          </div>
        </div>

        {backup ? (
          <div class="card signal tight build-banner" role="alert">
            <span>{t("build.backup.found", { time: timeOf(locale, backup.at) })}</span>
            <div class="row">
              <Button
                size="sm"
                onClick={() => {
                  const b = backup;
                  setBackup(null);
                  edit(() => b.scenario);
                }}
              >
                {t("build.backup.restore")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  clearBackup(id);
                  setBackup(null);
                }}
              >
                {t("build.backup.discard")}
              </Button>
            </div>
          </div>
        ) : null}

        <div class="segmented build-tabs" role="tablist" aria-label={t("build.tabs")} ref={tabsRef}>
          {(["outline", "edit", "checks"] as const).map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              id={`build-tab-${k}`}
              aria-selected={tab === k}
              aria-controls={`build-region-${k}`}
              onClick={() => setTab(k)}
            >
              {t(`build.tab.${k}`)}
              {k === "checks" && errorCount ? <span class="build-count error">{errorCount}</span> : null}
            </button>
          ))}
        </div>

        <FieldErrors.Provider value={fieldErrors}>
          <div class="builder" data-tab={tab}>
            <nav
              class="build-outline"
              id="build-region-outline"
              aria-label={t("build.outline")}
              role="tabpanel"
              aria-labelledby="build-tab-outline"
            >
              <Outline
                scenario={draft}
                current={current}
                issues={[...shownChecks.errors, ...shownChecks.warnings, ...shownChecks.conventions]}
                onSelect={select}
                onAdd={() => setAdding(true)}
              />
            </nav>
            <div
              class="build-form"
              id="build-region-edit"
              ref={formRef}
              role="tabpanel"
              aria-labelledby="build-tab-edit"
              data-path={current.kind === "node" ? `nodes.${current.id}` : current.id}
            >
              {current.kind === "node" ? (
                <NodeForm
                  scenario={draft}
                  id={current.id}
                  edit={edit}
                  onSelect={(nid) =>
                    setSelection(nid ? { kind: "node", id: nid } : { kind: "section", id: "overview" })
                  }
                />
              ) : (
                <SectionForm section={current.id} scenario={draft} edit={edit} />
              )}
            </div>
            <aside
              class="build-side"
              id="build-region-checks"
              role="tabpanel"
              aria-labelledby="build-tab-checks"
            >
              <ChecksPanel
                meta={meta}
                checks={shownChecks}
                onJump={jump}
                statuses={statuses}
                totalStrings={totalStrings}
                model={initial.translationModel}
                ensureSaved={save}
                onStatus={(loc, st) =>
                  setStatuses((cur) => {
                    const next = { ...cur };
                    if (st) next[loc] = st;
                    else delete next[loc];
                    return next;
                  })
                }
                onVisibility={setVisibility}
                onDelete={() => setConfirmDelete(true)}
              />
            </aside>
          </div>
        </FieldErrors.Provider>
      </main>

      {adding ? (
        <Dialog title={t("build.node.addTitle")} onClose={() => setAdding(false)}>
          <div class="choice-list">
            {NODE_TYPES.map((type) => (
              <button key={type} type="button" class="choice build-type-choice" onClick={() => addNode(type)}>
                <span class="node-type">{t(`build.type.${type}`)}</span>
                <span class="sub">{t(`build.type.${type}.help`)}</span>
              </button>
            ))}
          </div>
        </Dialog>
      ) : null}

      {confirmDelete ? (
        <Dialog title={t("build.delete")} onClose={() => setConfirmDelete(false)}>
          <div class="stack">
            <p>{t("build.deleteConfirm", { title: draft.title })}</p>
            <div class="row">
              <Button variant="danger" icon="trash" onClick={remove}>
                {t("action.delete")}
              </Button>
              <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
                {t("action.cancel")}
              </Button>
            </div>
          </div>
        </Dialog>
      ) : null}

      {conflict ? (
        <Dialog title={t("build.conflict.title")} onClose={() => setConflict(null)}>
          <div class="stack">
            <p>
              {conflict.meta
                ? t("build.conflict.body", { time: formatDateTime(locale, conflict.meta.updatedAt) })
                : t("build.conflict.bodyPlain")}
            </p>
            <div class="row">
              <Button
                variant="secondary"
                icon="arrow-left"
                onClick={() => {
                  clearBackup(id);
                  editSeq.current = savedSeq.current;
                  setConflict(null);
                  reload();
                }}
              >
                {t("build.conflict.reload")}
              </Button>
              <Button
                onClick={() => {
                  if (conflict.meta) expected.current = conflict.meta.updatedAt;
                  setConflict(null);
                  setSaveState("dirty");
                  save();
                }}
              >
                {t("build.conflict.keep")}
              </Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </Page>
  );
}

function Outline({
  scenario,
  current,
  issues,
  onSelect,
  onAdd,
}: {
  scenario: Scenario;
  current: Selection;
  issues: ValidationIssue[];
  onSelect: (s: Selection) => void;
  onAdd: () => void;
}) {
  const { t } = useI18n();
  const order = useMemo(() => outlineOrder(scenario), [scenario]);
  const counts = (sel: Selection) => {
    const mine = issuesFor(scenario, sel, issues);
    return {
      errors: mine.filter((i) => i.level === "error").length,
      warnings: mine.filter((i) => i.level === "warning").length,
    };
  };
  const dots = (sel: Selection) => {
    const c = counts(sel);
    return (
      <span class="build-dots">
        {c.errors ? (
          <span class="build-count error" title={t("build.outline.errors", { count: c.errors })}>
            {c.errors}
            <span class="visually-hidden"> {t("build.outline.errors", { count: c.errors })}</span>
          </span>
        ) : null}
        {c.warnings ? (
          <span class="build-count warning" title={t("build.outline.warnings", { count: c.warnings })}>
            {c.warnings}
            <span class="visually-hidden"> {t("build.outline.warnings", { count: c.warnings })}</span>
          </span>
        ) : null}
      </span>
    );
  };
  const nodeRow = (nid: string) => {
    const n = scenario.nodes[nid]!;
    const sel: Selection = { kind: "node", id: nid };
    const on = sameSelection(current, sel);
    return (
      <li key={nid}>
        <button
          type="button"
          class={on ? "current" : ""}
          aria-current={on ? "true" : undefined}
          onClick={() => onSelect(sel)}
        >
          <span class="node-type" title={t(`build.type.${n.type}`)}>
            {n.type}
          </span>
          <span class="build-outline-title" dir="auto" title={`${nid} · ${n.title}`}>
            {n.title || nid}
            {scenario.start === nid ? (
              <span class="visually-hidden"> ({t("build.node.isStart")})</span>
            ) : null}
          </span>
          {dots(sel)}
        </button>
      </li>
    );
  };
  return (
    <div class="build-outline-body">
      <ul class="node-map build-outline-list">
        {SECTIONS.map((sid: SectionId) => {
          const sel: Selection = { kind: "section", id: sid };
          const on = sameSelection(current, sel);
          return (
            <li key={sid}>
              <button
                type="button"
                class={on ? "current" : ""}
                aria-current={on ? "true" : undefined}
                onClick={() => onSelect(sel)}
              >
                <Icon name={SECTION_ICONS[sid]} />
                <span class="build-outline-title">{t(`build.section.${sid}`)}</span>
                {dots(sel)}
              </button>
            </li>
          );
        })}
      </ul>
      <div class="build-outline-head">
        <span class="eyebrow">{t("build.outline.nodes", { count: Object.keys(scenario.nodes).length })}</span>
        <Button variant="secondary" size="sm" icon="plus" onClick={onAdd}>
          {t("build.node.add")}
        </Button>
      </div>
      <ul class="node-map build-outline-list">{order.reachable.map(nodeRow)}</ul>
      {order.unreachable.length ? (
        <>
          <div class="build-outline-head">
            <span class="eyebrow">{t("build.outline.unreachable")}</span>
          </div>
          <ul class="node-map build-outline-list">{order.unreachable.map(nodeRow)}</ul>
        </>
      ) : null}
    </div>
  );
}

const SECTION_ICONS: Record<SectionId, string> = {
  overview: "file-text",
  roles: "users",
  scoring: "radar",
  injects: "zap",
  badges: "award",
};
